<template>
    <Form @submit="saveUser" :key="createMode">
        <div class="user-form flex flex-column overflow-hidden gap-3">
            <CustomInputNumber name="id" :label="$t('message.user.id')" v-model="editedUser.id" class="w-10"
                               disabled noPlaceholder/>
            <CustomInputText name="fullName" :label="$t('message.user.fullName')" v-model="editedUser.fullName"
                             class="w-10" required noPlaceholder
                             :customErrorMessage="getRequiredMessage(editedUser.fullName)"/>
            <CustomInputText name="email" :label="$t('message.user.email')" v-model="editedUser.email" class="w-10"
                             required noPlaceholder
                             :customErrorMessage="getEmailFormatMessage(editedUser.email)"/>
            <CustomSelectOneSystemRole name="systemRole" :label="$t('message.user.userType')"
                                       v-model="editedUser.systemRole" class="w-10" required noPlaceholder
                                       :customErrorMessage="getRequiredMessage(editedUser.systemRole)"/>
            <CustomSelectOneLaboratory v-if="editedUser.systemRole === 'LAB_USER'" name="whichLab"
                             :label="$t('message.user.whichLab')" v-model="editedUser.laboratoryId" class="w-10"
                             required noPlaceholder
                             :customErrorMessage="getRequiredMessage(editedUser.laboratoryId)"/>
            <div class="user-buttons flex align-self-center gap-4 mt-6">
                <CustomButton :label="$t('message.other.save')" type="submit" />
                <CustomButton v-if="createMode" :label="$t('message.other.cancel')" secondary
                              @click="hideForm" />
                <CustomButton v-else :label="$t('message.other.delete')" secondary
                              @click="deleteUser" />
            </div>
        </div>
    </Form>
</template>

<script>
    import {Form} from "vee-validate";
    import {
        createOrUpdateUser,
        deleteUser,
    } from "@/swagger/vue-api-client";
    import CustomInputText from "@/components/form/CustomInputText";
    import CustomSelectOneSystemRole from "@/components/form/CustomSelectOneSystemRole.vue";
    import CustomInputNumber from "@/components/form/CustomInputNumber.vue";
    import CustomButton from "@/components/form/CustomButton.vue";
    import CustomSelectOne from "@/components/form/inner/CustomSelectOne.vue";
    import { ValidateUtils } from "@/utils/ValidateUtils";
    import CustomSelectOneLaboratory from "@/components/form/CustomSelectOneLaboratory.vue";

    export default {
        name: "UserForm",

        components: {
            CustomSelectOneLaboratory,
            CustomSelectOneSystemRole,
            CustomInputText,
            Form,
            CustomInputNumber,
            CustomButton,
            CustomSelectOne,
        },

        props: {
            selectedUser: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                editedUser: {
                    id: null,
                    fullName: "",
                    email: "",
                    systemRole: null,
                    laboratoryId: null,
                },
                emailRegex: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                createMode: true,
                showErrorMessages: false,
            };
        },

        emits: ["change-element", "createMode", "hideForm"],

        mounted() {
            this.$emit("createMode", this.createMode);
        },

        methods: {
            validateData() {
                let isCorrect = ValidateUtils.validateNotEmpty(this.editedUser, ["fullName", "email", "systemRole"]);
                if (this.editedUser.systemRole === "LAB_USER") {
                    isCorrect = ValidateUtils.validateNotEmpty(this.editedUser, ["fullName", "email", "systemRole", "laboratoryId"]) && isCorrect;
                }
                if (this.editedUser.email) {
                    isCorrect = this.emailRegex.test(this.editedUser.email) && isCorrect;
                }
                this.showErrorMessages = !isCorrect;
                return isCorrect;
            },
            getRequiredMessage(value) {
                if (this.showErrorMessages && (value === null || value === "")) {
                    return this.$t("message.validations.required");
                }
                return "";
            },
            getEmailFormatMessage(value) {
                if (this.showErrorMessages && value && !this.emailRegex.test(this.editedUser.email)) {
                    return this.$t("message.validations.email");
                }
                return this.getRequiredMessage(value);
            },
            notifyChange() {
                this.$emit("change-element");
                this.$toast.add({
                    severity: "success",
                    summary: this.$t('message.other.success'),
                    detail: this.$t('message.other.dataSaved'),
                    life: 3000,
                });
            },
            notifyDelete() {
                this.$emit("change-element");
                this.$toast.add({
                    severity: "success",
                    summary: this.$t('message.other.success'),
                    detail: this.$t('message.other.elementDeleted'),
                    life: 3000,
                });
            },
            notifyError(error) {
                const message = error.response?.data?.message || this.$t('unknownError');
                this.$toast.add({
                    severity: "error",
                    summary: this.$t('message.other.error'),
                    detail: message,
                    life: 5000,
                });
            },
            saveUser() {
                if (this.validateData()) {
                    createOrUpdateUser({body: this.editedUser})
                        .then(() => {
                            this.notifyChange();
                        })
                        .catch((error) => {
                            this.notifyError(error);
                        });
                }
            },
            deleteUser() {
                deleteUser({id: this.editedUser.id})
                    .then(() => {
                        this.notifyDelete();
                        this.showErrorMessages = false;
                    })
                    .catch((error) => {
                        this.notifyError(error);
                    });
            },
            hideForm() {
                this.$emit("hideForm");
            },
        },

        watch: {
            selectedUser(newValue) {
                this.editedUser = _.cloneDeep(newValue);
                this.createMode = newValue.id === null || newValue.id === undefined || isNaN(newValue.id);
                this.$emit("createMode", this.createMode);
            },
        },
    };
</script>

<style lang="less">
    .user-form {
        .custom-input-text, .custom-input-number, .custom-select-one {
            padding-left: 170px;
            .custom-label {
                text-align: right;
                z-index: 999;
                position: absolute;
                transform: translateX(-100%);
                left: 160px;
            }
        }
        @media screen and (max-width: 650px) {
            align-items: end;
            .custom-input-text, .custom-input-number, .custom-select-one {
                padding-left: 80px;
                .custom-label {
                    left: 70px;
                }
            }
        }
    }
</style>
